/**
 * GrantShell — shared page wrapper for every user-facing Grant
 * Navigator surface. Renders `<AppNav />`, the site-wide compliance
 * disclaimer (from `site-settings`), and the grant-specific extended
 * disclaimer directly below it. Individual grant pages render their
 * content as children.
 *
 * Visual parity target: `/readiness` (Readiness.jsx). Layout mirrors
 * that page's inline `Shell` so users perceive Grant Readiness and
 * Credit Readiness as siblings — deliberate preparation for the
 * future Opportunity Readiness Dashboard.
 *
 * Above-the-fold budget (1366×768): the shell chrome (nav + both
 * disclaimers + top padding) must consume < 200px so that Grant
 * Detail's eligibility snapshot renders within the fold even on
 * accounts that also see the unverified-email banner. Disclaimers
 * are therefore rendered compactly (p-3, single-line icon+text).
 */
import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api } from "@/api/client";
import GrantDisclaimer from "@/components/grants/GrantDisclaimer";

export default function GrantShell({ children, testId = "grant-shell-root" }) {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => {
        if (!cancelled) setSiteSettings(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const disclaimer = siteSettings?.disclaimer_text;

  return (
    <div
      data-testid={testId}
      data-page-outer="grant-navigator"
      className="min-h-screen"
      style={{
        background: "var(--ds-color-background)",
        color: "var(--ds-color-ink)",
      }}
    >
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-6 pb-24">
        {disclaimer && (
          <details
            data-testid="grant-site-disclaimer"
            className="rounded-[var(--ds-radius-md)] px-3 py-2 text-[11px] leading-relaxed"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
              color: "var(--ds-color-ink-muted)",
            }}
          >
            <summary className="flex items-center gap-2 cursor-pointer list-none">
              <Info
                className="w-3 h-3 flex-shrink-0"
                style={{ color: "var(--ds-color-accent)" }}
              />
              <span
                className="uppercase tracking-[0.14em] text-[10px] font-semibold"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                Compliance notice
              </span>
              <span
                className="ml-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                — tap to expand
              </span>
            </summary>
            <p className="mt-2 pl-5">{disclaimer}</p>
          </details>
        )}
        <GrantDisclaimer />
        {children}
      </main>
    </div>
  );
}
